import React, { useRef } from "react";
import { Typography, useTheme } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import Divider from "@mui/material/Divider";
import "../JaldiLab.css";
import {
  DividerBorderStyle,
  dimensionStyle,
  getTextContentHeading,
  h1style,
  h2style,
  h3style,
  paraStyle,
  referenceHtml,
  referenceTootKit,
  subHeadingStyle,
  sectionStyle,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import toiltBlock1 from "../../../assets/Images/toilet_block1.jpeg";
import toiltBlock2 from "../../../assets/Images/toilet_block2.jpeg";
import toiltBlock3 from "../../../assets/Images/toilet_block3.jpg";
import toiltBlock4 from "../../../assets/Images/toilet_block4.jpeg";
import toiltBlock5 from "../../../assets/Images/toilet_block5.jpeg";
import accessibleToilt from "../../../assets/Images/accessible_toilet.jpeg";
import familyWashroom from "../../../assets/Images/familyWashroom.jpeg";
import familyToilet from "../../../assets/Images/family_toilet.jpeg";
import babySeat from "../../../assets/Images/babySeat.jpeg";
import babySeatProtection from "../../../assets/Images/baby_seat_protection.jpeg";
import diapherWashroom from "../../../assets/Images/diapher_washroom.jpeg";
import waterFacilities from "../../../assets/Images/water_facilities.jpg";
import drinkingWaterFacilities from "../../../assets/Images/Drinking_water_facility.jpg";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import sanitaryImg1 from "../../../assets/Images/Sanitary Images/Washroom.png";
import wasteDisposal from "../../../assets/Images/Sanitary Images/Waste_disposal_system.png";
import disposalBins from "../../../assets/Images/Sanitary Images/disposalBins.jpeg";
import { FigureDesc66, FigureDesc67, ImageDesc69, ImageDesc70, ImageDesc71, ImageDesc72, ImageDesc73, ImageDesc74, ImageDesc75, ImageDesc76, ImageDesc77, ImageDesc78, ImageDesc79 } from "../../../utils/tmp/ImageDescription/SanitaryProvisionID";

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};

const SanitaryProvisions = ({ goToSection }) => {
  const theme = useTheme();
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content: "Harmonised Guidelines 2021, p. 200.",
    },
    {
      content: "Harmonised Guidelines 2021, p. 194. ",
    },
    {
      content: "Harmonised Guidelines 2021, p. 229.",
    },
    {
      content: "Harmonised Guidelines 2021, p. 229.",
    },
    {
      content: "Harmonised Guidelines 2021, p. 193, Table 4.1.",
    },
    {
      content: "Harmonised Guidelines 2021, p. 194.",
    },
    {
      content: "Harmonised Guidelines 2021, p. Pg 210",
    },
    {
      content: "Harmonised Guidelines 2021, p. 115, Section 3.12",
    },
    {
      content: "Harmonised Guidelines 2021, p. 190, Section 4.8.",
    },
    {
      content: "Harmonised Guidelines 2021, p. 190, Section 4.8.",
    },
  ];

  const sanitaryProvisionsObjectives = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Plan for amenities that will{" "}
        <span style={{ color: "#DC002D" }}>
          address the needs of diverse users{" "}
        </span>{" "}
        and provide a comfortable and efficient environment for all user groups;
      </>,
      <>
        Ensure that the design of all amenities{" "}
        <span style={{ color: "#DC002D" }}>
          maintains the privacy and comfort of all users
        </span>
        , and that the amenities are clean, well-maintained, and ready for use
        at all points of time, as per the needs of the users; and
      </>,
      <>
        <span style={{ color: "#DC002D" }}>
          Provide adequate accessible facilities and signage{" "}
        </span>{" "}
        to ensure easy access to all amenities.
      </>,
    ],
    isOrdered: true,
    isImage: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "Amenities must be provided within the Court Complex as well as in self-contained blocks such as the VWDC Complex or the e-Seva Kendra.",
      "Amenities must be present at multiple strategic locations for easy access from other Court facilities, with sanitary provisions in particular being provided on each floor.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const designConsiderationsObject = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations for All Amenities",
    data: [
      <>
        Amenities should be planned in a manner ensuring minimal noise
        disruption for other users of the Court Complex. For further details of
        Acoustics, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          Annexure 5
        </span>
        .
      </>,
      <>
        Amenities must be designed in an accessible manner for diverse user
        groups. Accessible and appropriate signage and wayfinding systems should
        be provided, including information in local languages. For further
        details of the Accessibility Plan and the Information and Wayfinding
        Systems, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() =>
            goToSection(
              "accessibilityNavigationPlan",
              "headingAccessiblityPlan"
            )
          }
        >
          PART D, Section 3.
        </span>
      </>,
      "They must be designed keeping in mind the appropriate functional size required, based on the number of users.",
      "A calm, soothing atmosphere must be provided through the use of natural lighting and ventilation.",
      "Adequate storage space must be provided, depending on the requirements of the amenities outlined below.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const washroomsDataObject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Plan for washrooms in the Court Complex depending on the{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          ratio of users expected to use the Complex
        </span>
        , and taking into consideration the local building bye-laws;
      </>,
      <>
        <span style={{ color: "#DC002D" }}>
          {" "}
          Consider local socio-cultural preferences for providing different
          kinds of washrooms
        </span>
        , including an all-inclusive and universally accessible washroom system;
      </>,
      <>
        <span style={{ color: "#DC002D" }}>
          {" "}
          Provide family washrooms to meet the diverse needs of various users
        </span>
        , including parents with infants, young children, elderly family
        members, caregivers, expectant mothers, persons with disabilities,
        individuals recovering from physical injuries, etc.;
      </>,
      <>
        Ensure regular maintenance checks to{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          facilitate hygienic and well-maintained sanitary provisions;{" "}
        </span>
      </>,
      <>
        Ensure that{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          accessible washrooms are reserved and kept unoccupied
        </span>
        , irrespective of the frequency of their use, to ensure they remain
        dedicated for their intended purpose; and
      </>,
      <>
        <span style={{ color: "#DC002D" }}>
          {" "}
          Provide adequate training to staff{" "}
        </span>{" "}
        for providing accessible services and emergency support.
      </>,
    ],
    isOrdered: true,
    isImage: true,
  };

  const adjacenciesWashrooms = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "At the site level, hygienic public toilets which are accessible and family-friendly, including a set of washrooms for male, female, and gender-neutral/unisex users must be provided for all users.",
      "Within the Court Complex, each floor/wing of the building must have a set of washrooms which are accessible and family-friendly, for male, female, gender-neutral/unisex users. These washrooms must be positioned at the end of corridors, and must be easily accessible from common areas of the building.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const specificDesignWashrooms = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      <>
        In each toilet block, at least one toilet cubicle suitable for use by
        persons with ambulatory disabilities must be provided.
        {reference(referenceData[0], 1)}
      </>,
      <>
        Additionally, in each block, at least 25% Indian-style water closet
        cubicles must be provided.{reference(referenceData[1], 2)}
      </>,
      <>
        At least one child-sized water closet and wash basin must be provided in
        each of the male and female toilets. The water closet seat must be at a
        height of between <span style={dimensionStyle}>250 mm</span> to{" "}
        <span style={dimensionStyle}>350 mm</span> from the finished floor
        level.
        {reference(referenceData[2], 3)} At least one urinal, mounted at a
        height of about <span style={dimensionStyle}>400 mm</span> from the
        finished floor level, must be provided in the male toilet for young
        children.{reference(referenceData[3], 4)} The height of the wash basin
        must be at about <span style={dimensionStyle}>550 mm </span>from the
        finished floor level.{reference(referenceData[4], 5)}
      </>,
      <>
        Provision for baby protection seats, baby and adult changing stations,
        must be provided in each toilet block. For further details of changing
        stations, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() =>
            goToSection("familyChildFriendlyServices", "diaperChangingStations")
          }
        >
          Section 3.3.A.
        </span>
      </>,
      "A vending machine for sanitary napkins and diapers must be provided in each toilet block.",
      "Hygienic disposal units for sanitary napkins, diapers, etc., must be provided.",
      "Well-illuminated access routes must be provided to the public toilets, with adequate and accessible signage.",
      "The toilets must have non-slippery and levelled surfaces.",
      "Grooves and gratings provided in the toilet complex must be designed in such a manner that they do not become access barriers for diverse user groups, wheeling devices, etc.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const designConsiderationsData = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      <>
        Accessible washrooms must be provided in such a manner that any
        wheelchair user/ambulant disabled person would not be required to travel
        more than <span style={dimensionStyle}>30 m</span> on the same floor.
        {reference(referenceData[5], 6)}
      </>,
      <>
        Minimum internal dimensions of{" "}
        <span style={dimensionStyle}>2200 mm x 2000 mm</span> must be provided.
        {reference(referenceData[6], 7)}
      </>,
      <>
        All fixtures and utilities must provide a clear space of{" "}
        <span style={dimensionStyle}>900 mm x 1200 mm</span> for wheelchair
        users to access them.
      </>,
      "Gender-neutral accessible washrooms must include provision for baby protection seats and diaper changing stations for adults and children.",
      "Accessible features including mirrors, grab bars, etc., must be provided.",
      "Emergency features including emergency alarm switch, pull cord, etc., must be provided.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const familySpecificDesignConsiderationsData = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      "Toilets, urinals, and washbasins in family-friendly washrooms must be designed for the use of both adults and children,",
      "Family washrooms must include provision for baby protection seats, diaper changing stations for adults and children, and seating provisions for nursing mothers.",
      "Adequate space must be provided within the washrooms to manoeuvre and park prams.",
      "Accessible features including mirror, grab bars, etc., must be provided.",
      "Emergency features including emergency alarm switch, pull cord, etc., must be provided.",
      "The family toilet (especially near the main waiting areas) must be equipped with a wall-mounted foldable chair, for comfortably sitting and donning or doffing prosthetics.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const drinkingWaterObj = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Ensure consistent access to clean and safe drinking water for all{" "}
        <span style={{ color: "#DC002D" }}> diverse user groups </span>{" "}
        including persons with disability, children, etc., by installing{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          accessible drinking water facilities.
        </span>
      </>,
      <>
        Ensure regular maintenance checks to{" "}
        <span style={{ color: "#DC002D" }}>
          facilitate hygienic and well-maintained drinking water provision{" "}
        </span>{" "}
        complying with health and safety regulations.
      </>,
      <>
        Promote sustainability by implementing{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          efficient water management practices{" "}
        </span>
        , such as the use of water-saving fixtures and regular maintenance to
        prevent leaks.
      </>,
    ],
    isOrdered: true,
    isImage: true,
  };
  const drinkingWaterAdj = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "Adequate, accessible drinking water facilities must be provided at the site-level of the Court Complex.",
      "Within the Court Complex, each floor/wing of the building must have adequate, accessible drinking water facilities for all users.",
    ],
    isOrdered: true,
    isImage: true,
  };
  const drinkingWaterDes = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      "Well-illuminated access routes must be provided to the drinking water facilities, with adequate and accessible signage, tactile pavers, appropriate slope, gratings, and drainage covers for water spillage and to maintain hygiene.",
      <>
        A clear space of <span style={dimensionStyle}>900 mm x 1200 mm</span>{" "}
        must be provided in front of the water fountains or faucets, as
        manoeuvring spaces for wheelchairs and other mobility devices such as{" "}
        baby prams, rollators, etc.
        {reference(referenceData[7], 8)}
      </>,
      <>
        A clear knee space which is <span style={dimensionStyle}>750 mm</span>{" "}
        wide and <span style={dimensionStyle}>200 mm</span> deep must be
        provided. The height must range between{" "}
        <span style={dimensionStyle}>680 mm</span> to{" "}
        <span style={dimensionStyle}>750 mm</span> from the bottom of the apron
        and floor or ground. It is recommended to make provisions of drinking
        water facilities at two different heights: between{" "}
        <span style={dimensionStyle}>750 mm</span> to
        <span style={dimensionStyle}> 800 mm</span> range for wheelchair users
        or persons with low height, and at{" "}
        <span style={dimensionStyle}>900 mm</span> for the convenience of
        standing adults.
        {reference(referenceData[8], 9)}
      </>,
      <>
        Drinking water provisions must be wall-mounted in an alcove, to ensure
        that no hazard is created for persons with visual impairments.
        {reference(referenceData[9], 10)}
      </>,
      "Drinking water fountains must also provide stations for bottle fillers.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const wasteDisposalObj = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        <span style={{ color: "#DC002D" }}>
          Provide appropriate waste disposal options{" "}
        </span>{" "}
        for diapers, sanitary napkins, and other sanitary items; and
      </>,
      <>
        Ensure that the disposal systems are{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          accessible, clean, and sanitised.
        </span>
      </>,
    ],
    isOrdered: true,
    isImage: true,
  };

  const wasteDisposalAdj = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "Adequate, accessible waste disposal systems must be provided at the site-level of the Court Complex.",
      "Within the Court Complex, each floor/wing of the building must have adequate, accessible waste disposal facilities on each floor and within all washrooms. ",
    ],
    isOrdered: true,
    isImage: true,
  };
  const wasteDisposalDes = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      "Clearly marked and strategically placed designated waste disposal bins must be provided for disposal of sanitary waste.",
      "Lidded bins must be provided to reduce odour and maintain hygiene.",
      "Routine cleaning and emptying of the bins must be scheduled to prevent overflowing.",
    ],
    isOrdered: true,
    isImage: true,
  };

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>
        <h2 id="commonAmenities" style={h2style}>
          <span style={{ fontSize: "24px" }}>3. </span>COMMON AMENITIES
        </h2>
        <p style={paraStyle}>
          The Court Complex must provide for various amenities for all users. In
          instances where the Court Complex may be located at a distance from
          publicly available facilities, it must be designed in a
          self-sufficient manner to ensure that all possible amenities are made
          available on-site. Some of the provisions that are critical in every
          Court Complex include family and child-friendly facilities such as
          creches, sanitary provisions, availability of food & drinking water
          facilities, medical services, and vendors for legal services such as
          ATMs/Bank services, reprographic services and stationery providers.
        </p>
        {getTextContentHeading(sanitaryProvisionsObjectives)}
        {getTextContentHeading(adjacenciesObject)}
        {getTextContentHeading(designConsiderationsObject)}

        <h3 id="sanitaryProvisions" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.1. </span> Sanitary Provisions
        </h3>
        <p style={paraStyle}>
          All Court Complexes must have adequate sanitary provisions to meet the
          needs of all diverse users. Sanitary provisions include washrooms,
          drinking water facilities, and waste disposal units.
        </p>
        <DividerBorderStyle />
        <h4 id="washrooms" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.1.</span>A. Washrooms
        </h4>
        {getTextContentHeading(washroomsDataObject)}
        <DividerBorderStyle />
        <h3 id="publictoiletlocks" style={subHeadingStyle}>
          I. Public Toilet Blocks
        </h3>
        {getTextContentHeading(adjacenciesWashrooms)}
        {getTextContentHeading(specificDesignWashrooms)}

        <CaptionWithBorders1
          image={[toiltBlock1]}
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          caption={"Image 69: Example of a public toilet block "}
          captionPadding="25px"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc69}
        />

        <CaptionWithBorders1
          image={[toiltBlock2]}
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          caption={
            "Image 70: Example of child-friendly facilities in a public toilet block"
          }
          zoomImgStyle={{ height: "500px" }}
          captionPadding="25px"
          imageDescription={ImageDesc70}
        />

        <CaptionWithBorders1
          image={[toiltBlock3]}
          caption={
            "Image 71: Example of an ambulant toilet cubicle in a public toilet block"
          }
          creditLabel="Source"
          imageCredit="Duracube.com.au"
          zoomImgStyle={{ height: "500px" }}
          captionPadding="25px"
          imageDescription={ImageDesc71}
        />

        <CaptionWithBorders1
          image={[toiltBlock4]}
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          caption={
            "Image 72: Example of an accessible toilet cubicle in a public toilet block"
          }
          zoomImgStyle={{ height: "500px" }}
          captionPadding="25px"
          imageDescription={ImageDesc72}
        />

        <CaptionWithBorders1
          image={[toiltBlock5]}
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          caption={
            "Image 73: Example of a toilet cubicle with child protection seat in a public toilet block"
          }
          zoomImgStyle={{ height: "500px" }}
          captionPadding="25px"
          imageDescription={ImageDesc73}
        />

        <CaptionWithBorders1
          image={[sanitaryImg1]}
          isZoom={true}
          caption={
            "Figure 66: Typical floor plan for an accessible washroom block"
          }
          zoomImgStyle={{ height: "500px" }}
          imageDescription={FigureDesc66}
        />
        <DividerBorderStyle />

        <h3 id="genderneutralaccessiblewashrooms" style={subHeadingStyle}>
          II. Gender-Neutral Accessible Washrooms
        </h3>
        {getTextContentHeading(designConsiderationsData)}
        <CaptionWithBorders1
          image={[accessibleToilt]}
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          caption="Image 74: Example of a gender-neutral accessible toilet"
          zoomImgStyle={{ height: "500px" }}
          captionPadding="25px"
          imageDescription={ImageDesc74}
        />

        <DividerBorderStyle />
        <h3 id="familyFriendlyWashrooms" style={subHeadingStyle}>
          III. Family-Friendly Washrooms
        </h3>
        {getTextContentHeading(familySpecificDesignConsiderationsData)}

        <CaptionWithBorders1
          image={[familyWashroom]}
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          caption="Image 75: Example of an accessible family toilet"
          zoomImgStyle={{ height: "500px" }}
          captionPadding="25px"
          imageDescription={ImageDesc75}
        />
        <CaptionWithBorders1
          image={[babySeat, babySeatProtection]}
          zoomImgStyle={{ height: "500px" }}
        />

        <CaptionWithBorders1
          image={[diapherWashroom, familyToilet]}
          zoomImgStyle={{ height: "500px" }}
          caption="Image 76: Examples of signage and other components like baby
          diaper changing station and baby protection seat at
          family-friendly washrooms"
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          imageDescription={ImageDesc76}
        />

        <DividerBorderStyle />
        <h4 id="drinkingWater" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.1.</span>B. Drinking Water
        </h4>
        {getTextContentHeading(drinkingWaterObj)}
        {getTextContentHeading(drinkingWaterAdj)}
        {getTextContentHeading(drinkingWaterDes)}

        <CaptionWithBorders1
          image={[waterFacilities]}
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          caption="Image 77: Example of accessible ramp for external drinking water facilities"
          zoomImgStyle={{ height: "500px" }}
          captionPadding="25px"
          imageDescription={ImageDesc77}
        />
        <CaptionWithBorders1
          image={[drinkingWaterFacilities]}
          creditLabel="Image Credit"
          imageCredit="Brinda Sastry"
          caption="Image 78: Example of accessible drinking water facilities"
          zoomImgStyle={{ height: "500px" }}
          captionPadding="25px"
          imageDescription={ImageDesc78}
        />

        <DividerBorderStyle />
        <h4 id="sanitaryWaste" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.1.</span>C. Sanitary Waste
          Disposal System
        </h4>
        {getTextContentHeading(wasteDisposalObj)}
        {getTextContentHeading(wasteDisposalAdj)}
        {getTextContentHeading(wasteDisposalDes)}

        <CaptionWithBorders1
          image={[disposalBins]}
          caption="Image 79: Example of types and location of sanitary disposal bins"
          height={"500px"}
          captionPadding="25px"
          imageDescription={ImageDesc79}
        />
        <CaptionWithBorders1
          image={[wasteDisposal]}
          caption="Figure 67: Representation of Components of a Waste Disposal System"
          captionPadding="15px"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={FigureDesc67}
        />

        <h2
          ref={referencesRef}
          id="sanitaryProvisionsRef"
          style={referenceStyle}
        >
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default SanitaryProvisions;
